import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchData, saveLogin } from "../../redux/slices/mainDataSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightToBracket,
  faArrowRightFromBracket,
  faXmark,
  faCheck,
  faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import request from "../../api/request";
import Tooltip from "../../components/Tooltip";

const safeISODate = (val) => {
  try {
    const d = new Date(val);
    if (!val || isNaN(d.getTime()) || d.getFullYear() < 1950) return "—";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return "—";
  }
};

const safeDay = (val) => {
  try {
    const d = new Date(val);
    if (!val || isNaN(d.getTime()) || d.getFullYear() < 1950) return "—";
    const days = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    return days[d.getDay()];
  } catch {
    return "—";
  }
};

const safeTime = (val) => {
  try {
    const d = new Date(val);
    if (!val || isNaN(d.getTime()) || d.getFullYear() < 1950) return "—";
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'م' : 'ص';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    return `${hoursStr}:${minutes}:${seconds} ${ampm}`;
  } catch {
    return "—";
  }
};

const getPaydateEndLocal = (val) => {
  if (!val) return null;
  try {
    let d;
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, day] = val.split("-").map(Number);
      d = new Date(y, m - 1, day);
    } else if (typeof val === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
      const [day, m, y] = val.split("/").map(Number);
      d = new Date(y, m - 1, day);
    } else if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
      const [day, m, y] = val.split("-").map(Number);
      d = new Date(y, m - 1, day);
    } else {
      d = new Date(val);
    }
    if (isNaN(d.getTime())) return null;
    d.setHours(23, 59, 59, 999);
    return d;
  } catch {
    return null;
  }
};

export default function Schools() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isAuth = useSelector((state) => state.main.isAuthenticated);
  const schoolsData = useSelector((state) => state.main.data?.schoolData ?? []);
  const token = useSelector((state) => state.main.token) || JSON.parse(localStorage.getItem("userCredentials"))?.token;

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | on | off | unpaid
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [schoolHistoryStatus, setSchoolHistoryStatus] = useState({}); // { rgmw: hasHistory }
  const [loadingHistoryStatus, setLoadingHistoryStatus] = useState(false);
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [todaySchools, setTodaySchools] = useState([]);
  const [loadingToday, setLoadingToday] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scroller = document.querySelector(".content");
      const scrollY = scroller ? scroller.scrollTop : window.scrollY;
      setIsScrolled(scrollY > 10);
    };

    const scroller = document.querySelector(".content");
    if (scroller) {
      scroller.addEventListener("scroll", handleScroll);
    } else {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scroller) scroller.removeEventListener("scroll", handleScroll);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isAuth) {
      const stored = JSON.parse(localStorage.getItem("userCredentials"));
      if (stored) dispatch(saveLogin(stored));
      else navigate("/");
      return;
    }

    if (!Array.isArray(schoolsData) || schoolsData.length === 0) {
      dispatch(fetchData(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  useEffect(() => {
    const fetchAllHistoryStatus = async () => {
      if (!Array.isArray(schoolsData) || schoolsData.length === 0 || !token) return;

      setLoadingHistoryStatus(true);
      const status = {};
      for (const school of schoolsData) {
        try {
          const response = await request.get(`/api/alfares/schools/login-history/${school.rgmw}/${token}`);
          status[school.rgmw] = (response.data.history || []).length > 0;
        } catch (error) {
          status[school.rgmw] = false;
        }
      }
      setSchoolHistoryStatus(status);
      setLoadingHistoryStatus(false);
    };

    fetchAllHistoryStatus();
  }, [schoolsData, token]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (schoolsData || [])
      .filter((s) => {
        const name = String(s.schoolname || "").toLowerCase();
        const matchesSearch = q ? name.includes(q) : true;

        const paydateEnd = getPaydateEndLocal(s.paydate);
        const hasPaydate = Boolean(paydateEnd);
        const isExpired = Boolean(paydateEnd && paydateEnd < new Date());
        const isOn = s.onoff === "on";

        const matchesStatus =
          statusFilter === "all"
            ? true
            : statusFilter === "on"
            ? isOn
            : statusFilter === "off"
            ? !isOn
            : // لم تدفع: on وتاريخ الاشتراك ما زال ساري المفعول
            statusFilter === "unpaid"
            ? isOn && hasPaydate && !isExpired
            : true;

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) =>
        String(a.schoolname || "").localeCompare(
          String(b.schoolname || ""),
          "ar"
        )
      );
  }, [schoolsData, search, statusFilter]);

  const showLoginInfo = async (school) => {
    setSelectedSchool(school);
    setShowLoginModal(true);
    setLoadingHistory(true);
    
    try {
      const response = await request.get(`/api/alfares/schools/login-history/${school.rgmw}/${token}`);
      const history = response.data.history || [];
      // Sort by action_time ascending on frontend as well to ensure correct order (oldest first)
      const sortedHistory = [...history].sort((a, b) => {
        const timeA = new Date(a.action_time).getTime();
        const timeB = new Date(b.action_time).getTime();
        if (timeA !== timeB) return timeA - timeB;
        // If times are equal, sort by id ascending
        return (a.id || 0) - (b.id || 0);
      });
      setLoginHistory(sortedHistory);
    } catch (error) {
      console.error("Error fetching login history:", error);
      setLoginHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchTodaySchools = async () => {
    setShowTodayModal(true);
    setLoadingToday(true);
    const todayStr = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const results = [];
    for (const school of schoolsData) {
      try {
        const response = await request.get(`/api/alfares/schools/login-history/${school.rgmw}/${token}`);
        const history = response.data.history || [];
        const loggedToday = history.some((r) => {
          if (!r.action_time) return false;
          return r.action_time.toString().startsWith(todayStr);
        });
        if (loggedToday) results.push(school);
      } catch {
        // skip
      }
    }
    setTodaySchools(results);
    setLoadingToday(false);
  };

  return (
    <div className="schools-wrapper">
      {/* ===== مودال المدارس الداخلة اليوم ===== */}
      {showTodayModal && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            padding: "20px",
          }}
          onClick={() => setShowTodayModal(false)}
        >
          <div
            style={{
              background: "#ffffff",
              border: "1px solid var(--border)",
              borderRadius: "16px",
              padding: "24px",
              width: "100%",
              maxWidth: "480px",
              maxHeight: "80vh",
              overflow: "auto",
              direction: "rtl",
              boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* رأس المودال */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", paddingBottom: "12px", borderBottom: "1px solid var(--border)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <FontAwesomeIcon icon={faCalendarCheck} style={{ color: "#22c55e", fontSize: "18px" }} />
                <span style={{ color: "var(--text)", fontWeight: "600", fontSize: "16px" }}>المدارس الداخلة اليوم</span>
              </div>
              <button
                onClick={() => setShowTodayModal(false)}
                style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", fontSize: "18px", padding: "4px" }}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {/* المحتوى */}
            {loadingToday ? (
              <div style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
                <div style={{ marginBottom: "12px", fontSize: "24px" }}>⏳</div>
                جارٍ البحث...
              </div>
            ) : todaySchools.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
                لا توجد مدارس سجّلت دخولاً اليوم
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "12px", color: "#22c55e", fontSize: "13px" }}>
                  {todaySchools.length} مدرسة دخلت اليوم
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {todaySchools.map((s, i) => (
                    <div
                      key={s.rgmw}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 14px",
                        background: "rgba(34,197,94,0.07)",
                        border: "1px solid rgba(34,197,94,0.2)",
                        borderRadius: "10px",
                        cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onClick={() => { setShowTodayModal(false); showLoginInfo(s); }}
                    >
                      <span style={{ color: "var(--muted)", fontSize: "12px", minWidth: "22px" }}>{i + 1}</span>
                      <span style={{ color: "var(--text)", flex: 1 }}>{s.schoolname || "—"}</span>
                      <FontAwesomeIcon icon={faArrowRightToBracket} style={{ color: "#22c55e", fontSize: "13px" }} />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showLoginModal && selectedSchool && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "20px",
          }}
          onClick={() => setShowLoginModal(false)}
        >
          <div
              className="login-history-modal"
              style={{
                borderRadius: "16px",
                padding: "24px",
                width: "100%",
                maxWidth: "900px",
                maxHeight: "90vh",
                overflow: "auto",
                direction: "rtl",
                border: "1px solid var(--border)",
                boxShadow: "0 20px 60px rgba(13, 35, 32, 0.18)",
                background: "#ffffff",
              }}
              onClick={(e) => e.stopPropagation()}
            >
            <div className="login-history-header">
              <h3 className="login-history-title">
                <span className="login-history-title-prefix">سجل الدخول - </span>
                {selectedSchool.schoolname}
              </h3>
              <div className="login-history-count">
                <span className="login-history-count-prefix">عدد مرات تسجيل الدخول: </span>
                {loginHistory.filter(r => r.action_type === 1).length}
              </div>
            </div>

            {loadingHistory ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                جاري تحميل السجل...
              </div>
            ) : loginHistory.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
                لا يوجد سجل دخول للمدرسة
              </div>
            ) : (
              <div style={{ 
                maxHeight: "450px", // تحديد الحد الأقصى للارتفاع لعرض 10 سجلات
                overflowY: "auto", // تفعيل التمرير العمودي
                overflowX: "auto" 
              }}>
                <table className="login-history-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 10 }}> {/* تثبيت رأس الجدول */}
                    <tr>
                      <th style={{ border: "1px solid var(--border)", padding: "6px", textAlign: "right", background: "#1e3a5f", fontWeight: "normal" }}>#</th>
                      <th style={{ border: "1px solid var(--border)", padding: "6px", textAlign: "right", background: "#1e3a5f", fontWeight: "normal" }}>وقت الإجراء</th>
                      <th style={{ border: "1px solid var(--border)", padding: "6px", textAlign: "right", background: "#1e3a5f", fontWeight: "normal" }}>الإجراء</th>
                      <th style={{ border: "1px solid var(--border)", padding: "6px", textAlign: "right", background: "#1e3a5f", fontWeight: "normal" }}>عنوان IP</th>
                      <th style={{ border: "1px solid var(--border)", padding: "6px", textAlign: "right", background: "#1e3a5f", fontWeight: "normal" }}>الجهاز</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistory.map((record, index) => (
                      <tr key={record.id || index}>
                        <td style={{ border: "1px solid var(--border)", padding: "6px", textAlign: "center" }}>{index + 1}</td>
                        <td style={{ border: "1px solid var(--border)", padding: "6px" }}>
                          <div>
                            <span style={{ color: "var(--accent)", marginLeft: "8px" }}>{safeDay(record.action_time)}</span>
                            {safeISODate(record.action_time)}
                          </div>
                          <div style={{ color: "var(--muted)", fontSize: "12px" }}>{safeTime(record.action_time)}</div>
                        </td>
                        <td style={{ border: "1px solid var(--border)", padding: "6px", textAlign: "center" }}>
                          {record.action_type === 1 ? (
                            <FontAwesomeIcon icon={faArrowRightToBracket} style={{ color: "#22c55e", fontSize: "16px" }} />
                          ) : (
                            <FontAwesomeIcon icon={faArrowRightFromBracket} style={{ color: "#ef4444", fontSize: "16px" }} />
                          )}
                        </td>
                        <td style={{ border: "1px solid var(--border)", padding: "6px" }}>{record.ip_address || "-"}</td>
                        <td style={{ border: "1px solid var(--border)", padding: "6px" }}>{record.device_name || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                className="btn"
                onClick={() => setShowLoginModal(false)}
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`toolbar schools-toolbar ${isScrolled ? "scrolled" : ""}`}
      >
        <div className="search-side">
          <input
            className="input"
            placeholder="ابحث باسم المدرسة…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Tooltip content="المدارس الداخلة اليوم">
            <button
              className="today-login-btn"
              onClick={fetchTodaySchools}
           
            >
              <FontAwesomeIcon icon={faCalendarCheck} />
            </button>
          </Tooltip>
          <span className="results-badge">{filtered.length} سجل</span>
        </div>

        <nav className="schools-toolbar-tabs">
          {[
            { key: "all", label: "الكل" },
            { key: "on", label: "المدارس العاملة" },
            { key: "off", label: "المدارس المغلقة" },
            { key: "unpaid", label: "لم تدفع" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`nav-square-btn ${
                statusFilter === tab.key ? "active" : ""
              }`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="cards-grid">
        {filtered.map((school) => {
          const paydateEnd = getPaydateEndLocal(school.paydate);
          const isOn = school.onoff === "on";

          return (
            <div key={school.rgmw} className="school-card">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <h3
                    className="school-card__title"
                    style={{
                      fontWeight: 400,
                      color: "#C28E00",
                      textShadow: "0 1px 0 rgba(0,0,0,0.2)",
                    }}
                  >
                    {school.schoolname || "—"}
                  </h3>
                  <p className="school-card__sub">
                    الرقم الوطني للمدرسة: {school.rgmw}
                  </p>
                </div>
                <span
                  className={`badge ${
                    isOn ? "badge--success" : "badge--danger"
                  }`}
                >
                  {isOn ? "فعال" : "موقوف"}
                </span>
              </div>

              <div
                className="school-card__actions"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => navigate(`/schools/${school.rgmw}`)}
                  >
                    تفاصيل المدرسة
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={() => showLoginInfo(school)}
                    disabled={!loadingHistoryStatus && schoolHistoryStatus[school.rgmw] === false}
                    style={!loadingHistoryStatus && schoolHistoryStatus[school.rgmw] === false ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                  >
                    LogIn Info
                  </button>
                </div>

                {!isOn ? (
                  <span className="pay-icon pay-icon--off">
                    <FontAwesomeIcon icon={faXmark} />
                  </span>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span className="pay-icon pay-icon--paid">
                      <FontAwesomeIcon icon={faCheck} />
                    </span>
                    {paydateEnd && paydateEnd > new Date() && (
                      <span
                        dir="ltr"
                        style={{
                          fontSize: "12px",
                          color: "#C28E00",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ExD: {safeISODate(school.paydate)}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .pay-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          font-size: 11px;
          flex-shrink: 0;
        }
        .pay-icon--paid {
          background: #dcfce7;
          color: #16a34a;
        }
        .pay-icon--off {
          background: #fee2e2;
          color: #dc2626;
        }
        .schools-wrapper {
          min-height: 100%;
          direction: rtl;
          margin-top: -20px;
        }

        .schools-toolbar {
          position: sticky;
          top: 3px;
          z-index: 1000;
          margin: 0 0 40px 0 !important;
          width: 100% !important;
          box-sizing: border-box !important;
          background: #ffffff !important;
          border: 1px solid var(--border) !important;
          border-radius: 20px !important;
          padding: 12px 25px !important;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
          flex-wrap: wrap;
          box-shadow: 0 15px 35px -10px rgba(0,0,0,0.5) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
        }

        .schools-toolbar.scrolled {
          background: #ffffff !important;
          border-color: var(--accent) !important;
          box-shadow: 0 10px 30px -5px rgba(13,35,32,0.12) !important;
          padding: 8px 25px !important;
          border-radius: 14px !important;
          top: 0;
        }

        .search-side {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 280px;
        }

        .search-side .input {
          width: 100%;
          max-width: 400px;
          height: 40px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid var(--border);
          color: var(--text);
          padding: 0 15px;
          font-family: inherit;
          font-size: 14px;
          margin-right: -12px;
        }

        .results-badge {
          font-size: 14px;
          font-weight: 800;
          color: var(--accent);
          white-space: nowrap;
        }

        .today-login-btn {
          display: none;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          min-width: 38px;
          border-radius: 10px;
          border: 1px solid rgba(34, 197, 94, 0.4);
          background: rgba(34, 197, 94, 0.08);
          color: #22c55e;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .today-login-btn:hover {
          background: rgba(34, 197, 94, 0.18);
          border-color: #22c55e;
          transform: scale(1.05);
        }

        @media (max-width: 768px) {
          .today-login-btn {
            display: flex;
          }
        }

        .schools-toolbar-tabs {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          justify-content: center;
        }

        @media (max-width: 768px) {
          .schools-toolbar {
            padding: 15px !important;
            flex-direction: column;
            align-items: stretch;
            gap: 15px;
            position: relative !important;
            top: auto !important;
            z-index: 1 !important;
          }
          .search-side {
            min-width: 100%;
            justify-content: space-between;
            margin-right: 0;
          }
          .search-side .input {
            max-width: none;
            flex: 1;
          }
          .schools-toolbar-tabs {
            justify-content: flex-start;
            overflow-x: auto;
            padding-bottom: 5px;
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .schools-toolbar-tabs::-webkit-scrollbar {
            display: none;
          }
          .nav-square-btn {
            padding: 6px 12px;
            font-size: 12px;
          }
        }

        .nav-square-btn {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text);
          padding: 8px 16px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: inherit;
          font-weight: 500;
          font-size: 13px;
          white-space: nowrap;
        }

        .nav-square-btn:hover {
          background: rgba(14, 165, 233, 0.1);
          color: var(--accent);
          border-color: var(--accent);
          transform: translateY(-1px);
        }

        .nav-square-btn.active {
          background: var(--accent) !important;
          color: white !important;
          border-color: var(--accent) !important;
          box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
        }

        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 15px;
        }

        .school-card {
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 15px !important;
          min-height: 110px !important;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          transition: 0.3s;
        }

        .school-card:hover {
          transform: translateY(-3px);
          border-color: var(--accent);
          box-shadow: var(--shadow-hover);
        }

        .login-history-modal {
          background: #ffffff; /* لون خلفية صلب (غير شفاف) للوضع الفاتح */
        }

        .login-history-modal {
          background: #ffffff;
        }

        .login-history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--border);
        }

        .login-history-title {
          margin: 0;
          color: var(--accent);
          font-weight: normal;
        }

        .login-history-count {
          padding: 8px 16px;
          background: rgba(14, 165, 233, 0.1);
          border-radius: 10px;
          color: var(--accent);
          font-weight: normal;
        }

        @media (max-width: 768px) {
          .login-history-title-prefix,
          .login-history-count-prefix {
            display: none;
          }
          
          .login-history-table {
            font-size: 12px;
          }
          
          .login-history-table th,
          .login-history-table td {
            padding: 4px !important;
          }
        }
      `}</style>
    </div>
  );
}
